import React, { useState, useRef, useCallback } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Typography,
    Box,
    Chip,
} from "@mui/material";
import {
    Description as QuoteIcon,
    ArchiveOutlined as ArchivedIcon,
} from "@mui/icons-material";
import QuoteService from "../../services/Quote.service";

/**
 * Type-to-search autocomplete for picking an existing quote.
 * Only fetches when the user types 2+ characters (debounced 250 ms).
 */
export default function QuotePicker({
    value,
    onChange,
    label = "Quote",
    disabled = false,
    error = "",
    size = "medium",
}) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);

    const handleInputChange = useCallback((_, inputVal, reason) => {
        clearTimeout(timerRef.current);

        if (reason !== "input") {
            setLoading(false);
            return;
        }

        const term = (inputVal || "").trim();
        setOptions([]);

        if (term.length < 2) {
            setLoading(false);
            return;
        }

        setLoading(true);
        timerRef.current = setTimeout(async () => {
            try {
                const res = await QuoteService.search(term);
                const results = Array.isArray(res.data)
                    ? res.data
                    : (res.data?.results ?? []);
                setOptions(results);
            } catch {
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, 250);
    }, []);

    const getLabel = (opt) => (opt ? `${opt.number || ""} ${opt.name || ""}`.trim() : "");

    return (
        <Autocomplete
            options={options}
            value={value}
            onChange={(_, newVal) => onChange(newVal)}
            onInputChange={handleInputChange}
            filterOptions={(x) => x}
            getOptionLabel={getLabel}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            loading={loading}
            disabled={disabled}
            noOptionsText="Type at least 2 characters to search…"
            renderOption={({ key, ...optProps }, opt) => (
                <Box
                    key={key}
                    component="li"
                    {...optProps}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        opacity: opt.is_archived ? 0.6 : 1,
                    }}
                >
                    <QuoteIcon
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {opt.number} {opt.name}
                    </Typography>
                    {opt.is_archived && (
                        <Chip
                            icon={<ArchivedIcon />}
                            label="Archived"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                        />
                    )}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size={size}
                    error={Boolean(error)}
                    helperText={error || ""}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
